import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@material-ui/core";
import { useDeviceTypes } from "halifax";
import { Route } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";

import { CoreCarsCheckout, REVIEWED_CAR_DETAILS_CHECKOUT } from "redmond";
import { isCorpTenant } from "@capone/common";
import { getPickupLocationLabel } from "./utils/getPickupLocationLabel";
import { useDispatch } from "react-redux";
import { CarBookConnectorProps } from "./container";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_HOME,
} from "../../utils/paths";
import "./styles.scss";
import { BookingErrorModal } from "./components/BookingErrorModal";
import { BookingInProgressModal } from "./components/BookingInProgressModal";
import { BookingSuccessModal } from "./components/BookingSuccessModal";
import { trackEvent } from "../../api/v1/analytics/trackEvent";
import { CAR_CONFIRMATION_TITLE, PORTAL_TITLE } from "../../lang/textConstants";
import { config } from "../../api/config";
import { MobileCarBookWorkflowStep } from "./components/capone/MobileCarBookWorkflow/component";
import { setCarShopResults, setSelectedVehicle } from "../shop/actions/actions";
import { getCoreCarAvailabilityLineItem } from "../availability/reducer/utils/carAvailabilityHelperFunctions";
import { CarExtraInfoCallState } from "../shop/reducer";
import { redoSearch, resetBookErrors } from "./actions/actions";
import {setCarSearch, setCarSearchEntryPoint, setCarSearchEntryPointsAll} from "../search/actions/actions";
import { setCarAvailabilityEntryPoint, setCarAvailabilityEntryPointsAll } from "../availability/actions/actions";
import { parseQueryString } from "./utils/parseQueryString";

const { DesktopCarBookWorkflow } = isCorpTenant(config.TENANT)
    // eslint-disable-next-line @typescript-eslint/no-require-imports
  ? require("./components/capone-corporate/DesktopCarBookWorkflow")
  : require("./components/capone/DesktopCarBookWorkflow");
const { MobileCarBookWorkflow } = isCorpTenant(config.TENANT)
  ? require("./components/capone-corporate/MobileCarBookWorkflow")
  : require("./components/capone/MobileCarBookWorkflow");

export interface ICarBookProps
  extends RouteComponentProps,
    CarBookConnectorProps {
  core?: CoreCarsCheckout;
}

export const CarBook = (props: ICarBookProps) => {
  const {
    location,
    history,
    selectedVehicle,
    isBookingValid,
    reviewedCarDetailsCheckoutProperties,
    setPriceQuote,
    setIsTravelApp,
    core,
  } = props;
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [checkoutStep, setCheckoutStep] =
    useState<MobileCarBookWorkflowStep>(0);

  const dispatch = useDispatch();

  const coreVehicleId = core
    ? getCoreCarAvailabilityLineItem(
        core.car,
        core.context,
        null,
        getPickupLocationLabel(core.search.pickupLocation),
        0
      ).id
    : undefined;

  useEffect(() => {
    if (core) {
      const { entryType, entryTypesAll } = core;

      const locationLabel = getPickupLocationLabel(core.search.pickupLocation);

      const vehicle = getCoreCarAvailabilityLineItem(
        core.car,
        core.context,
        null,
        locationLabel,
        0
      );
      if (selectedVehicle?.id !== vehicle.id) {
        dispatch(redoSearch());
      } else if (isCorpTenant(config.TENANT)) {
        dispatch(resetBookErrors());
      }
      dispatch(setSelectedVehicle(vehicle));
      dispatch(
        setCarShopResults({
          carExtraInfoCallState: CarExtraInfoCallState.Success,
          // @ts-ignore
          extraInfo: core.shop,
        })
      );
      dispatch(
        setCarSearch({
          // @ts-ignore
          dropOffLocation: core.search.dropoffLocation,
          dropOffDate: new Date(core.search.dropoffTime),
          dropOffTime: core.search.dropoffTime,
          // @ts-ignore
          pickUpLocation: core.search.pickupLocation,
          pickUpDate: new Date(core.search.pickupTime),
          pickUpTime: core.search.pickupTime,
          driverAge: core.search.driverAge,
        })
      );

      if (entryType) {
        dispatch(setCarSearchEntryPoint(entryType));
        dispatch(setCarAvailabilityEntryPoint(entryType));
      }

      if (entryTypesAll) {
        dispatch(setCarSearchEntryPointsAll(entryTypesAll));
        dispatch(setCarAvailabilityEntryPointsAll(entryTypesAll));
      }
    }
    // Re-sync whenever the funnel hands off a different vehicle, so re-entering
    // checkout with another car replaces the previous selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreVehicleId]);

  // `carShop.selectedVehicle` is never cleared on navigation, so on re-entry it
  // still holds the previously booked car until the effect above replaces it.
  // The workflows read it during their mount effect to build the travel wallet
  // offers request, and child effects run before this component's, so rendering
  // them any earlier would request offers for the wrong vehicle.
  const isCoreVehicleSynced =
    core == null || selectedVehicle?.id === coreVehicleId;

  useEffect(() => {
    document.title = CAR_CONFIRMATION_TITLE;

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    setPriceQuote(null);
    if (!selectedVehicle && core == null) {
      history.push(`${PATH_HOME}`);
    }
  }, [selectedVehicle]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isBookingValid) {
      trackEvent({
        eventName: REVIEWED_CAR_DETAILS_CHECKOUT,
        properties: { ...reviewedCarDetailsCheckoutProperties.properties },
        encryptedProperties: [
          ...reviewedCarDetailsCheckoutProperties.encryptedProperties,
        ],
      });
    }
  }, [isBookingValid]);

  const parsedQueryString = useMemo(
      () => parseQueryString(history),
      [history.location.search]
  );

  useEffect(() => {
      setIsTravelApp(!!parsedQueryString?.isTravelApp && matchesMobile);
  }, [parsedQueryString]);

  return (
    <Box
      className={clsx(
        "car-book-root",
        { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
        { mobile: matchesMobile }
      )}
    >
      <Route path={PATH_BOOK} exact>
        <>
          <Box className="car-book-container">
            {matchesDesktop && selectedVehicle && isCoreVehicleSynced && (
              <DesktopCarBookWorkflow key={selectedVehicle.id} />
            )}
            {matchesMobile && selectedVehicle && isCoreVehicleSynced && (
              <MobileCarBookWorkflow
                key={selectedVehicle.id}
                checkoutStep={checkoutStep}
                setCheckoutStep={setCheckoutStep}
              />
            )}
          </Box>
          <BookingErrorModal setCheckoutStep={setCheckoutStep} core={core} />
          <BookingInProgressModal />
        </>
      </Route>
      <Route path={PATH_BOOK_CONFIRMATION}>
        <BookingSuccessModal />
      </Route>
    </Box>
  );
};
